import { c as _c } from "react/compiler-runtime";
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { match } from 'ts-pattern';
import { useMemo } from 'react';
import { GROUP_HEADER_ROW_HEIGHT, HEADER_CELL_BORDER_BOTTOM, THEAD_BORDER } from '../../constants/borderStyles';
import { SELECTION_COLUMN_WIDTH } from '../../constants/selectionColumn';
import { EagColumn } from '../../types/EagColumn';
import { EagColumnOrGroup } from '../../types/EagColumnGroup';
import { EagColumnState } from '../../types/EagColumnState';
import { isEagColumnGroup } from '../../utils/isEagColumnGroup';
import { ActionHeaderCell } from './components/ActionHeaderCell/ActionHeaderCell';
import { GroupHeaderRow } from './components/GroupHeaderRow/GroupHeaderRow';
import { HeaderCell } from './components/HeaderCell/HeaderCell';
import { SelectAllCheckbox } from './components/SelectAllCheckbox/SelectAllCheckbox';

// DndContext is intentionally absent here — it lives in EagGridTable (the parent),
// wrapping the outer <div> instead of <table>. This prevents dnd-kit accessibility
// <div>s from being injected as invalid children of <table>.
//
// Drag state (activeDragId, overColumnId, dropSide) is read directly from the
// EagGridStore by HeaderCell — no prop drilling through this layer.
//
// Column menu state (showColumnMenu, showPinning, showAutosize, filterState,
// and all callbacks) is provided via ColumnMenuContext by EagGridTable —
// no prop drilling through this layer either.

export type HeaderRowProps = {
  columns: EagColumn[];
  columnStates: EagColumnState[];
  onSort: (key: string) => void;
  onResizeStart: (colKey: string, startX: number, colIndex: number) => void;
  sortableIds: string[];
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  columnConfig: EagColumnOrGroup[];
  columnGroupsVisible: boolean;
  // Pinning edge keys — used to compute isLastPinnedLeft / isFirstPinnedRight per cell
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  actionColumnWidth?: number;
  headerHeightPx?: number;
  // Maps column key → <col> DOM index in colgroup. Accounts for filler position.
  colIndexMap: Map<string, number>;
  // Selection mode — controls whether to show SelectAllCheckbox or empty <th>
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
};
const hasAnyGroup = (config: EagColumnOrGroup[]): boolean => config.some(isEagColumnGroup);
export function HeaderRow(props) {
  const $ = _c(84);
  let t0;
  if ($[0] !== props.columnStates) {
    t0 = new Map(props.columnStates.map(_temp));
    $[0] = props.columnStates;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const columnStateMap = t0;
  let t1;
  if ($[2] !== props.columns) {
    t1 = new Set(props.columns.map(_temp2));
    $[2] = props.columns;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const visibleKeys = t1;
  let t2;
  if ($[4] !== props.columnConfig || $[5] !== props.columnGroupsVisible) {
    t2 = props.columnGroupsVisible && hasAnyGroup(props.columnConfig);
    $[4] = props.columnConfig;
    $[5] = props.columnGroupsVisible;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const showGroupRow = t2;
  let T0;
  let stickyTop;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== columnStateMap || $[8] !== props.actionColumnWidth || $[9] !== props.colIndexMap || $[10] !== props.columnConfig || $[11] !== props.columns || $[12] !== props.firstPinnedRightKey || $[13] !== props.lastPinnedLeftKey || $[14] !== props.onResizeStart || $[15] !== props.onSort || $[16] !== props.pinnedLeftOffsets || $[17] !== props.pinnedRightOffsets || $[18] !== props.rowSelectMode || $[19] !== props.sortableIds || $[20] !== showGroupRow || $[21] !== visibleKeys) {
    let t11;
    if ($[32] !== columnStateMap) {
      t11 = col_0 => {
        const cs_0 = columnStateMap.get(col_0.key);
        return cs_0?.pinned !== "RIGHT";
      };
      $[32] = columnStateMap;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    const nonRightPinnedColumns = props.columns.filter(t11);
    let t12;
    if ($[34] !== columnStateMap) {
      t12 = col_1 => {
        const cs_1 = columnStateMap.get(col_1.key);
        return cs_1?.pinned === "RIGHT";
      };
      $[34] = columnStateMap;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    const rightPinnedColumns = props.columns.filter(t12);
    const nonRightPinnedCount = nonRightPinnedColumns.length;
    stickyTop = showGroupRow ? GROUP_HEADER_ROW_HEIGHT : 0;
    T0 = SortableContext;
    t9 = props.sortableIds;
    t10 = horizontalListSortingStrategy;
    t7 = THEAD_BORDER;
    t8 = showGroupRow && <GroupHeaderRow columnConfig={props.columnConfig} visibleKeys={visibleKeys} actionColumnWidth={props.actionColumnWidth} nonRightPinnedCount={nonRightPinnedCount} />;
    if ($[36] !== props.rowSelectMode || $[37] !== stickyTop) {
      let t13;
      if ($[39] !== stickyTop) {
        t13 = () => <SelectAllCheckbox stickyTop={stickyTop} />;
        $[39] = stickyTop;
        $[40] = t13;
      } else {
        t13 = $[40];
      }
      let t14;
      if ($[41] !== stickyTop) {
        t14 = () => <th className="p-0 bg-white" style={{
          width: SELECTION_COLUMN_WIDTH,
          position: "sticky",
          top: stickyTop,
          left: 0,
          zIndex: "var(--z-content-loader)",
          ...HEADER_CELL_BORDER_BOTTOM
        }} />;
        $[41] = stickyTop;
        $[42] = t14;
      } else {
        t14 = $[42];
      }
      t3 = match(props.rowSelectMode).with("MULTIPLE", t13).with("SINGLE", t14).with("NONE", _temp3).exhaustive();
      $[36] = props.rowSelectMode;
      $[37] = stickyTop;
      $[38] = t3;
    } else {
      t3 = $[38];
    }
    let t13;
    if ($[43] !== columnStateMap || $[44] !== props.colIndexMap || $[45] !== props.firstPinnedRightKey || $[46] !== props.lastPinnedLeftKey || $[47] !== props.onResizeStart || $[48] !== props.onSort || $[49] !== props.pinnedLeftOffsets || $[50] !== props.pinnedRightOffsets || $[51] !== stickyTop) {
      t13 = column => {
        const columnState = columnStateMap.get(column.key);
        if (!columnState) {
          return null;
        }
        return <HeaderCell key={column.key} column={column} columnState={columnState} colIndex={props.colIndexMap.get(column.key) ?? 0} onSort={props.onSort} onResizeStart={props.onResizeStart} pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)} pinnedRightOffset={props.pinnedRightOffsets.get(column.key)} isLastPinnedLeft={props.lastPinnedLeftKey === column.key} isFirstPinnedRight={props.firstPinnedRightKey === column.key} stickyTop={stickyTop} />;
      };
      $[43] = columnStateMap;
      $[44] = props.colIndexMap;
      $[45] = props.firstPinnedRightKey;
      $[46] = props.lastPinnedLeftKey;
      $[47] = props.onResizeStart;
      $[48] = props.onSort;
      $[49] = props.pinnedLeftOffsets;
      $[50] = props.pinnedRightOffsets;
      $[51] = stickyTop;
      $[52] = t13;
    } else {
      t13 = $[52];
    }
    t4 = nonRightPinnedColumns.map(t13);
    if ($[53] !== stickyTop) {
      t5 = <th aria-hidden={true} style={{
        position: "sticky",
        top: stickyTop,
        background: "white",
        ...HEADER_CELL_BORDER_BOTTOM
      }} />;
      $[53] = stickyTop;
      $[54] = t5;
    } else {
      t5 = $[54];
    }
    let t14;
    if ($[55] !== columnStateMap || $[56] !== props.colIndexMap || $[57] !== props.firstPinnedRightKey || $[58] !== props.lastPinnedLeftKey || $[59] !== props.onResizeStart || $[60] !== props.onSort || $[61] !== props.pinnedLeftOffsets || $[62] !== props.pinnedRightOffsets || $[63] !== stickyTop) {
      t14 = column_0 => {
        const columnState_0 = columnStateMap.get(column_0.key);
        if (!columnState_0) {
          return null;
        }
        return <HeaderCell key={column_0.key} column={column_0} columnState={columnState_0} colIndex={props.colIndexMap.get(column_0.key) ?? 0} onSort={props.onSort} onResizeStart={props.onResizeStart} pinnedLeftOffset={props.pinnedLeftOffsets.get(column_0.key)} pinnedRightOffset={props.pinnedRightOffsets.get(column_0.key)} isLastPinnedLeft={props.lastPinnedLeftKey === column_0.key} isFirstPinnedRight={props.firstPinnedRightKey === column_0.key} stickyTop={stickyTop} />;
      };
      $[55] = columnStateMap;
      $[56] = props.colIndexMap;
      $[57] = props.firstPinnedRightKey;
      $[58] = props.lastPinnedLeftKey;
      $[59] = props.onResizeStart;
      $[60] = props.onSort;
      $[61] = props.pinnedLeftOffsets;
      $[62] = props.pinnedRightOffsets;
      $[63] = stickyTop;
      $[64] = t14;
    } else {
      t14 = $[64];
    }
    t6 = rightPinnedColumns.map(t14);
    $[7] = columnStateMap;
    $[8] = props.actionColumnWidth;
    $[9] = props.colIndexMap;
    $[10] = props.columnConfig;
    $[11] = props.columns;
    $[12] = props.firstPinnedRightKey;
    $[13] = props.lastPinnedLeftKey;
    $[14] = props.onResizeStart;
    $[15] = props.onSort;
    $[16] = props.pinnedLeftOffsets;
    $[17] = props.pinnedRightOffsets;
    $[18] = props.rowSelectMode;
    $[19] = props.sortableIds;
    $[20] = showGroupRow;
    $[21] = visibleKeys;
    $[22] = T0;
    $[23] = stickyTop;
    $[24] = t10;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[22];
    stickyTop = $[23];
    t10 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t11;
  if ($[65] !== props.actionColumnWidth || $[66] !== props.headerHeightPx || $[67] !== stickyTop) {
    t11 = props.actionColumnWidth ? <ActionHeaderCell actionColumnWidth={props.actionColumnWidth} heightPx={props.headerHeightPx ?? 40} stickyTop={stickyTop} /> : null;
    $[65] = props.actionColumnWidth;
    $[66] = props.headerHeightPx;
    $[67] = stickyTop;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] !== t11 || $[70] !== t3 || $[71] !== t4 || $[72] !== t5 || $[73] !== t6) {
    t12 = <tr>{t3}{t4}{t5}{t6}{t11}</tr>;
    $[69] = t11;
    $[70] = t3;
    $[71] = t4;
    $[72] = t5;
    $[73] = t6;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  let t13;
  if ($[75] !== t12 || $[76] !== t7 || $[77] !== t8) {
    t13 = <thead style={t7}>{t8}{t12}</thead>;
    $[75] = t12;
    $[76] = t7;
    $[77] = t8;
    $[78] = t13;
  } else {
    t13 = $[78];
  }
  let t14;
  if ($[79] !== T0 || $[80] !== t10 || $[81] !== t13 || $[82] !== t9) {
    t14 = <T0 items={t9} strategy={t10}>{t13}</T0>;
    $[79] = T0;
    $[80] = t10;
    $[81] = t13;
    $[82] = t9;
    $[83] = t14;
  } else {
    t14 = $[83];
  }
  return t14;
}
function _temp3() {
  return null;
}
function _temp2(col) {
  return col.key;
}
function _temp(cs) {
  return [cs.key, cs];
}